'use client'

import { NewsFavicon } from '@/components/NewsFavicon'
import { getBiasLabel } from '@/utils/outlets'
import { useState } from 'react'

export default function BiasReliabilityChart({ outlets }) {
  const [hoveredOutlet, setHoveredOutlet] = useState(null)

  if (!outlets || outlets.length === 0) {
    return (
      <div className="w-full aspect-[4/3] bg-muted/30 rounded-sm border border-border flex items-center justify-center">
        <span className="text-muted-foreground">No outlet data available</span>
      </div>
    )
  }

  // Chart dimensions and margins
  const chartPadding = { top: 20, right: 20, bottom: 40, left: 100 }

  // Bias range: -30 to +30
  // Reliability range: 0 to 64
  const biasMin = -30
  const biasMax = 30
  const reliabilityMin = 0
  const reliabilityMax = 64

  // Convert bias value to percentage position (0-100%)
  const biasToPercent = (bias) => {
    return ((bias - biasMin) / (biasMax - biasMin)) * 100
  }

  // Convert reliability value to percentage position (0-100%, inverted for Y axis)
  const reliabilityToPercent = (reliability) => {
    return 100 - ((reliability - reliabilityMin) / (reliabilityMax - reliabilityMin)) * 100
  }

  // X-axis labels - simplified to Left, Center, Right
  const xLabels = [
    { value: -15, label: 'Left' },
    { value: 0, label: 'Center' },
    { value: 15, label: 'Right' },
  ]

  // Y-axis labels for reliability
  const yLabels = [
    { value: 56, label: 'Reliable' },
    { value: 40, label: 'Mostly Reliable' },
    { value: 24, label: 'Mixed' },
    { value: 8, label: 'Unreliable' },
  ]

  return (
    <div className="w-full">
      <div className="relative w-full aspect-[4/3] bg-background border border-border rounded-sm overflow-hidden">
        {/* Chart area */}
        <div
          className="absolute"
          style={{
            left: chartPadding.left,
            top: chartPadding.top,
            right: chartPadding.right,
            bottom: chartPadding.bottom,
          }}
        >
          {/* Background gradient zones */}
          <div className="absolute inset-0 flex">
            {/* Left zone - blue tint */}
            <div
              className="h-full bg-blue-500/10"
              style={{ width: `${biasToPercent(-6)}%` }}
            />
            {/* Center zone - neutral */}
            <div
              className="h-full bg-muted/30"
              style={{ width: `${biasToPercent(6) - biasToPercent(-6)}%` }}
            />
            {/* Right zone - red tint */}
            <div
              className="h-full bg-red-500/10"
              style={{ width: `${100 - biasToPercent(6)}%` }}
            />
          </div>

          {/* Vertical divider lines at zone boundaries */}
          <div
            className="absolute top-0 bottom-0 w-px bg-border"
            style={{ left: `${biasToPercent(-6)}%` }}
          />
          <div
            className="absolute top-0 bottom-0 w-px bg-border"
            style={{ left: `${biasToPercent(6)}%` }}
          />

          {/* Horizontal grid lines at reliability levels */}
          {yLabels.map(({ value }) => (
            <div
              key={`h-${value}`}
              className="absolute left-0 right-0 h-px bg-border/50"
              style={{ top: `${reliabilityToPercent(value)}%` }}
            />
          ))}

          {/* Outlet favicons */}
          {outlets.map((outlet, index) => {
            const x = biasToPercent(outlet.bias)
            const y = reliabilityToPercent(outlet.reliability)
            const isHovered = hoveredOutlet === outlet.slug

            return (
              <div
                key={outlet.slug || index}
                className="absolute transform -translate-x-1/2 -translate-y-1/2 transition-all duration-200 cursor-pointer"
                style={{
                  left: `${x}%`,
                  top: `${y}%`,
                  zIndex: isHovered ? 50 : 10,
                }}
                onMouseEnter={() => setHoveredOutlet(outlet.slug)}
                onMouseLeave={() => setHoveredOutlet(null)}
              >
                <div
                  className={`relative transition-transform duration-200 ${isHovered ? 'scale-150' : 'scale-100'}`}
                >
                  <NewsFavicon
                    src={`${process.env.NEXT_PUBLIC_SUPABASE_URL}/storage/v1/object/public/public_assets/news-favicons/${outlet.slug}.jpg`}
                    faviconSize="sm"
                    alt={outlet.name}
                    className="rounded-full border border-border shadow-sm"
                  />
                  {isHovered && (
                    <div className="absolute left-1/2 -translate-x-1/2 -top-8 bg-foreground text-background text-xs px-2 py-1 rounded whitespace-nowrap z-50">
                      {outlet.name}
                      <div className="text-[10px] opacity-70">
                        {getBiasLabel(outlet.bias)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        {/* Y-axis labels */}
        <div
          className="absolute"
          style={{
            left: 8,
            top: chartPadding.top,
            bottom: chartPadding.bottom,
            width: chartPadding.left - 16,
          }}
        >
          {yLabels.map(({ value, label }) => (
            <div
              key={value}
              className="absolute right-0 text-xs font-sans text-muted-foreground text-right transform -translate-y-1/2"
              style={{ top: `${reliabilityToPercent(value)}%` }}
            >
              {label}
            </div>
          ))}
        </div>

        {/* X-axis labels */}
        <div
          className="absolute left-0 right-0 flex justify-between items-center"
          style={{
            bottom: 12,
            paddingLeft: chartPadding.left,
            paddingRight: chartPadding.right,
          }}
        >
          {xLabels.map(({ value, label }) => (
            <div
              key={value}
              className="text-sm font-sans font-medium text-muted-foreground"
              style={{
                position: 'absolute',
                left: `calc(${chartPadding.left}px + ${biasToPercent(value)}% * (100% - ${chartPadding.left + chartPadding.right}px) / 100%)`,
                transform: 'translateX(-50%)'
              }}
            >
              {label}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
